import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, TrendingUp, TrendingDown, Minus, Activity, Target, Award, Sparkles, BookOpen, Lock, RefreshCw,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart,
} from "recharts";

const MOOD_EMOJI = { great: "😄", good: "🙂", okay: "😐", low: "😕", rough: "😔" };
const MOOD_COLOR = { great: "#06D6A0", good: "#FFB347", okay: "#A0AEC0", low: "#7DA0E0", rough: "#E07A5F" };

const label = (c) => (c || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

function StatCard({ icon: Icon, tint, title, value, sub, delay }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="rounded-[2rem] border border-[#FFE8E3] bg-white p-6 shadow-warm">
      <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: tint + "22" }}>
        <Icon className="h-5 w-5" strokeWidth={2.5} style={{ color: tint }} />
      </span>
      <p className="mt-4 font-nunito text-3xl font-extrabold text-[#2D3748]">{value}</p>
      <p className="font-nunito text-sm font-bold text-[#2D3748]">{title}</p>
      {sub && <p className="font-quicksand text-xs text-[#718096]">{sub}</p>}
    </motion.div>
  );
}

function InsightsPanel() {
  const { user } = useAuth();
  const [tl, setTl] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const canAI = user?.plan && user.plan !== "free";

  const load = async () => {
    try {
      const { data } = await api.get("/insights/timeline");
      setTl(data.timeline || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/insights/patterns");
      setInsight(data.insight);
      if (data.timeline) setTl(data.timeline);
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setAnalyzing(false); }
  };

  const maxTasks = Math.max(1, ...(tl || []).map((d) => d.tasks || 0));
  const maxHabits = Math.max(1, ...(tl || []).map((d) => d.habits || 0));
  const dayLabel = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.27 }}
      className="mb-8 rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm" data-testid="mood-insights-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
          <BookOpen className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Mood &amp; Momentum · Last 14 days
        </h2>
        <button onClick={load} disabled={loading} className="rounded-full p-2 text-[#718096] hover:bg-[#FFF9F5] hover:text-[#FF7E67]"
          data-testid="insights-refresh-btn" title="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#FF7E67]" /></div>
      ) : !tl || tl.every((d) => !d.mood && !d.tasks && !d.habits) ? (
        <div className="mt-6 flex flex-col items-center rounded-2xl border-2 border-dashed border-[#FFE8E3] py-10 text-center">
          <Sparkles className="h-8 w-8 text-[#FFB347]" strokeWidth={2} />
          <p className="mt-3 font-nunito text-base font-bold text-[#2D3748]">Your patterns will appear here</p>
          <p className="font-quicksand text-sm text-[#718096]">Check in, log habits, and complete tasks — I&rsquo;ll spot the connections.</p>
        </div>
      ) : (
        <>
          {/* Mood row */}
          <div className="mt-6">
            <p className="mb-2 font-quicksand text-xs font-semibold uppercase tracking-wider text-[#718096]">Mood</p>
            <div className="grid grid-cols-14 gap-1.5" data-testid="mood-timeline" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
              {tl.map((d) => (
                <div key={d.date} className="flex flex-col items-center gap-1"
                  title={`${d.date}: ${d.mood ? label(d.mood) : "no entry"}`}>
                  <div className="flex h-9 w-full items-center justify-center rounded-lg text-lg"
                    style={{ background: d.mood ? MOOD_COLOR[d.mood] + "26" : "#FFF9F5" }}>
                    <span>{d.mood ? MOOD_EMOJI[d.mood] : ""}</span>
                  </div>
                  <span className="font-quicksand text-[10px] text-[#A0AEC0]">{dayLabel(d.date)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Task + habit bars */}
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 font-quicksand text-xs font-semibold uppercase tracking-wider text-[#718096]">Tasks done</p>
              <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
                {tl.map((d) => (
                  <div key={d.date} className="flex h-14 items-end justify-center" title={`${d.date}: ${d.tasks} tasks`}>
                    <div className="w-full rounded-md bg-sunrise" style={{ height: `${(d.tasks / maxTasks) * 100 || 4}%`, minHeight: 4, opacity: d.tasks ? 1 : 0.25 }} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 font-quicksand text-xs font-semibold uppercase tracking-wider text-[#718096]">Habits logged</p>
              <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}>
                {tl.map((d) => (
                  <div key={d.date} className="flex h-14 items-end justify-center" title={`${d.date}: ${d.habits} habits`}>
                    <div className="w-full rounded-md bg-[#06D6A0]" style={{ height: `${(d.habits / maxHabits) * 100 || 4}%`, minHeight: 4, opacity: d.habits ? 1 : 0.25 }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AI insight */}
          <div className="mt-6 rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-5" data-testid="pattern-insight-block">
            {insight ? (
              <>
                <div className="mb-2 flex items-center gap-2 text-[#C2410C]">
                  <Sparkles className="h-4 w-4" strokeWidth={2.5} />
                  <span className="font-nunito text-xs font-bold uppercase tracking-wider">Coach&rsquo;s pattern insight</span>
                </div>
                <p className="whitespace-pre-wrap font-quicksand text-sm text-[#2D3748]">{insight}</p>
                <button onClick={analyze} disabled={analyzing} data-testid="insights-regenerate-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 font-nunito text-xs font-bold text-[#C2410C] hover:bg-[#FFE8E3]">
                  {analyzing ? <><Loader2 className="h-3 w-3 animate-spin" /> Analyzing…</> : <><RefreshCw className="h-3 w-3" /> Regenerate</>}
                </button>
              </>
            ) : (
              <button onClick={analyze} disabled={analyzing} data-testid="insights-analyze-btn"
                className="inline-flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform hover:scale-[1.02] disabled:opacity-70">
                {analyzing
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Finding your patterns…</>
                  : canAI
                    ? <><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Analyze my patterns</>
                    : <><Lock className="h-4 w-4" strokeWidth={2.5} /> Analyze my patterns · Plus</>}
              </button>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}

export default function LifeScore() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/life-score/insights").then(({ data }) => setD(data)); }, []);

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  const cats = Object.entries(d.categories).sort((a, b) => b[1].score - a[1].score);
  const momentumIcon = d.momentum > 0 ? TrendingUp : d.momentum < 0 ? TrendingDown : Minus;
  const momentumTint = d.momentum > 0 ? "#06D6A0" : d.momentum < 0 ? "#E07A5F" : "#8FA3B8";
  const chartData = d.history.map((h) => ({
    date: new Date(h.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    score: h.overall,
  }));

  return (
    <div className="font-quicksand" data-testid="lifescore-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <p className="flex items-center gap-2 font-quicksand text-sm font-semibold text-[#FF7E67]">
          <Activity className="h-4 w-4" strokeWidth={2.5} /> Life Score
        </p>
        <h1 className="mt-1 font-nunito text-4xl font-extrabold tracking-tight text-[#2D3748] sm:text-5xl">Your Momentum</h1>
        <p className="mt-2 font-quicksand text-base font-medium text-[#718096]">Track how your world is trending across every area of life.</p>
      </motion.div>

      <div className="mb-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Sparkles} tint="#FF7E67" title="Life Score" value={d.overall} sub="of 100" delay={0.05} />
        <StatCard icon={momentumIcon} tint={momentumTint} title="Momentum" value={`${d.momentum > 0 ? "+" : ""}${d.momentum}`} sub="vs last week" delay={0.1} />
        <StatCard icon={Activity} tint="#FFB347" title="Consistency" value={`${d.consistency}%`} sub="habits, last 7 days" delay={0.15} />
        <StatCard icon={Award} tint="#06D6A0" title="Goals Done" value={d.completed_goals} sub={`${d.active_goals} active`} delay={0.2} />
      </div>

      {/* Trend chart */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="mb-8 rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
        <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
          <TrendingUp className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Score Trend
        </h2>
        {chartData.length < 2 ? (
          <div className="mt-6 flex flex-col items-center rounded-2xl border-2 border-dashed border-[#FFE8E3] py-12 text-center">
            <TrendingUp className="h-8 w-8 text-[#FFB347]" strokeWidth={2} />
            <p className="mt-3 font-nunito text-base font-bold text-[#2D3748]">Your trend is building</p>
            <p className="font-quicksand text-sm text-[#718096]">Check in daily — your score history charts here over time.</p>
          </div>
        ) : (
          <div className="mt-6 h-64" data-testid="score-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF7E67" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FFB347" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#FFE8E3" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#718096", fontSize: 12, fontFamily: "Quicksand" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: "#718096", fontSize: 12, fontFamily: "Quicksand" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 16, border: "1px solid #FFE8E3", fontFamily: "Quicksand" }} />
                <Area type="monotone" dataKey="score" stroke="#FF7E67" strokeWidth={3} fill="url(#scoreFill)" dot={{ fill: "#FF7E67", r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </motion.div>

      {/* Mood & Momentum Insights */}
      <InsightsPanel />

      {/* Category deep-dive */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="rounded-[2rem] border border-[#FFE8E3] bg-white p-8 shadow-warm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 font-nunito text-xl font-bold text-[#2D3748]">
            <Target className="h-5 w-5 text-[#FFB347]" strokeWidth={2.5} /> By Life Area
          </h2>
          <div className="flex gap-2 font-quicksand text-xs font-semibold">
            {d.best_category && <span className="rounded-full bg-[#E6FBF5] px-3 py-1 text-[#06D6A0]">Strongest: {label(d.best_category)}</span>}
            {d.focus_category && <span className="rounded-full bg-[#FFF4E6] px-3 py-1 text-[#B45309]">Focus: {label(d.focus_category)}</span>}
          </div>
        </div>
        {cats.length === 0 ? (
          <p className="mt-6 font-quicksand text-sm text-[#718096]">Set goals to see your life areas here.</p>
        ) : (
          <div className="mt-6 space-y-5" data-testid="category-breakdown">
            {cats.map(([c, v], i) => (
              <div key={c}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="font-nunito text-sm font-bold text-[#2D3748]">{label(c)}</span>
                  <span className="font-quicksand text-xs font-semibold text-[#718096]">{v.completed}/{v.goals} goals · {v.score}%</span>
                </div>
                <div className="h-3 w-full overflow-hidden rounded-full bg-[#FFF4E6]">
                  <motion.div className="h-full rounded-full bg-sunrise" initial={{ width: 0 }}
                    animate={{ width: `${v.score}%` }} transition={{ duration: 0.9, delay: 0.35 + i * 0.05, ease: [0.16, 1, 0.3, 1] }} />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-6 font-quicksand text-xs text-[#A0AEC0]">{d.disclaimer}</p>
      </motion.div>
    </div>
  );
}
